<template>
	<div class="g-timing">
		<div
			v-for="day in days"
			:key="day.weekDay"
			class="g-timing__day"
			:class="{
				'g-timing__day--active': isDayActive(day.weekDay),
				'g-timing__day--inactive': !isDayActive(day.weekDay),
			}"
		>
			<div class="g-timing__day-head">
				<button
					type="button"
					class="g-timing__toggle"
					:class="{ 'g-timing__toggle--on': isDayActive(day.weekDay) }"
					:aria-label="day.fullLabel"
					@click="toggleDay(day.weekDay)"
				>
					<svg
						v-if="isDayActive(day.weekDay)"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="3"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<polyline points="20 6 9 17 4 12" />
					</svg>
				</button>
				<span class="g-timing__day-name">{{ day.fullLabel }}</span>
				<span class="g-timing__day-summary">{{ daySummary(day.weekDay) }}</span>
			</div>

			<template v-if="isDayActive(day.weekDay)">
				<div class="g-timing__ranges">
					<div
						v-for="(range, idx) in getRanges(day.weekDay)"
						:key="`${day.weekDay}-${idx}`"
						class="g-timing__range"
					>
						<input
							type="time"
							class="g-timing__input g-timing__input--time"
							:value="rangeStart(range)"
							@input="updateRangeStart(day.weekDay, idx, $event.target.value)"
						/>
						<span class="g-timing__dash">—</span>
						<input
							type="time"
							class="g-timing__input g-timing__input--time"
							:value="rangeEnd(range)"
							@input="updateRangeEnd(day.weekDay, idx, $event.target.value)"
						/>
						<div class="g-timing__repeats">
							<input
								type="number"
								min="1"
								class="g-timing__input g-timing__input--repeats"
								:value="getRepeat(day.weekDay)"
								@input="updateRepeat(day.weekDay, $event.target.value)"
							/>
							<span class="g-timing__repeats-label">{{ $t('guaranteed.repeats') }}</span>
						</div>
						<button
							type="button"
							class="g-timing__remove"
							:aria-label="$t('common.remove') || 'Remove'"
							@click="removeRange(day.weekDay, idx)"
						>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<line x1="18" y1="6" x2="6" y2="18"/>
								<line x1="6" y1="6" x2="18" y2="18"/>
							</svg>
						</button>
					</div>
				</div>
				<button
					type="button"
					class="g-timing__add"
					@click="addRange(day.weekDay)"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
						<line x1="12" y1="5" x2="12" y2="19"/>
						<line x1="5" y1="12" x2="19" y2="12"/>
					</svg>
					{{ $t('guaranteed.add_time_range') }}
				</button>
			</template>
		</div>

		<!-- Weekly Preview -->
		<div class="g-timing__preview">
			<div class="g-timing__preview-head">
				<span class="g-timing__preview-title">{{ $t('guaranteed.weekly_preview') }}</span>
				<span class="g-timing__preview-summary">
					{{ $t('guaranteed.active_days_summary', { days: activeDayCount, ranges: totalRangeCount }) }}
				</span>
			</div>

			<div
				v-for="day in days"
				:key="`preview-${day.weekDay}`"
				class="g-timing__preview-row"
			>
				<span class="g-timing__preview-label">{{ day.shortLabel }}</span>
				<div class="g-timing__preview-track">
					<div
						v-for="h in 24"
						:key="h"
						class="g-timing__preview-cell"
						:class="{ 'g-timing__preview-cell--on': isHourActive(day.weekDay, h - 1) }"
						:title="hourTooltip(day.weekDay, h - 1)"
					/>
				</div>
			</div>

			<div class="g-timing__preview-axis">
				<span class="g-timing__preview-label-spacer" />
				<div class="g-timing__preview-axis-grid">
					<span v-for="h in 24" :key="`h-${h}`">{{ (h - 1) % 3 === 0 ? String(h - 1).padStart(2, '0') : '' }}</span>
				</div>
			</div>

			<p class="g-timing__preview-help">{{ $t('guaranteed.preview_help') }}</p>
		</div>
	</div>
</template>


<script>
const MODEL = Object.freeze({ prop: 'value', event: 'input' });

const HHMMSS_RE = /^(\d{2}):(\d{2}):(\d{2})$/;
const HHMM_RE = /^(\d{2}):(\d{2})$/;

function parseRangeBoundary(s) {
	// returns 'HH:MM'
	if (!s) return '';
	const m = HHMMSS_RE.exec(s) || HHMM_RE.exec(s);
	if (!m) return '';
	return `${m[1]}:${m[2]}`;
}

function buildTimeString(value) {
	const hours = Math.floor(value / 3600).toString().padStart(2, '0');
	const minutes = Math.floor((value - hours * 3600) / 60).toString().padStart(2, '0');

	return `${hours}:${minutes}`;
}

function buildRangeString(start, end) {
	const s = start ? `${start}:00` : '00:00:00';
	const e = end ? `${end}:59` : '00:00:59';
	return `${s}-${e}`;
}

export default {
	name: 'GuaranteedTimingRules',

	model: MODEL,

	props: {
		[MODEL.prop]: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			// ISO-style 0..6 with 0=Sunday; we render Mon..Sun in UI but store weekday number per spec.
			days: [
				{ weekDay: 1, key: 'mon', fullLabel: this.$t('guaranteed.weekday_mon'), shortLabel: this.$t('guaranteed.weekday_short_mon') },
				{ weekDay: 2, key: 'tue', fullLabel: this.$t('guaranteed.weekday_tue'), shortLabel: this.$t('guaranteed.weekday_short_tue') },
				{ weekDay: 3, key: 'wed', fullLabel: this.$t('guaranteed.weekday_wed'), shortLabel: this.$t('guaranteed.weekday_short_wed') },
				{ weekDay: 4, key: 'thu', fullLabel: this.$t('guaranteed.weekday_thu'), shortLabel: this.$t('guaranteed.weekday_short_thu') },
				{ weekDay: 5, key: 'fri', fullLabel: this.$t('guaranteed.weekday_fri'), shortLabel: this.$t('guaranteed.weekday_short_fri') },
				{ weekDay: 6, key: 'sat', fullLabel: this.$t('guaranteed.weekday_sat'), shortLabel: this.$t('guaranteed.weekday_short_sat') },
				{ weekDay: 0, key: 'sun', fullLabel: this.$t('guaranteed.weekday_sun'), shortLabel: this.$t('guaranteed.weekday_short_sun') },
			],
		};
	},

	computed: {
		activeDayCount() {
			return (this[MODEL.prop] || []).filter((r) => r.ranges && r.ranges.length > 0).length;
		},
		totalRangeCount() {
			return (this[MODEL.prop] || []).reduce((sum, r) => sum + (r.ranges ? r.ranges.length : 0), 0);
		},
	},

	methods: {
		findDayEntry(weekDay) {
			return (this[MODEL.prop] || []).find((r) => r.week_day === weekDay) || null;
		},

		isDayActive(weekDay) {
			const entry = this.findDayEntry(weekDay);
			return !!entry && Array.isArray(entry.ranges) && entry.ranges.length > 0;
		},

		getRanges(weekDay) {
			const entry = this.findDayEntry(weekDay);
			return entry && entry.ranges ? entry.ranges : [];
		},

		getRepeat(weekDay) {
			const entry = this.findDayEntry(weekDay);
			return entry && entry.repeat ? entry.repeat : 1;
		},

		rangeStart(range) {
			return buildTimeString(range[0]);
		},

		rangeEnd(range) {
			return buildTimeString(range[1]);
		},

		daySummary(weekDay) {
			const ranges = this.getRanges(weekDay);
			if (ranges.length === 0) return this.$t('guaranteed.inactive_day_full');
			const repeats = this.getRepeat(weekDay);
			const rangeWord = ranges.length === 1
				? this.$t('guaranteed.range_label_singular')
				: this.$t('guaranteed.ranges_label');
			return `${ranges.length} ${rangeWord} · ${repeats} ${this.$t('guaranteed.repeats_per_cycle')}`;
		},

		isHourActive(weekDay, hour) {
			const ranges = this.getRanges(weekDay);
			for (const r of ranges) {
				if (!r || r.length != 2) {
					continue;
				}

				const sH = parseInt(r[0] / 3600, 10);
				const eH = parseInt(r[1] / 3600, 10);

				if (Number.isNaN(sH) || Number.isNaN(eH)) continue;
				if (hour >= sH && hour <= eH) return true;
			}
			return false;
		},

		hourTooltip(weekDay, hour) {
			return this.isHourActive(weekDay, hour)
				? `${String(hour).padStart(2, '0')}:00 — ${String(hour).padStart(2, '0')}:59`
				: '';
		},

		emit(next) {
			this.$emit(MODEL.event, next);
		},

		cloneList() {
			return JSON.parse(JSON.stringify(this[MODEL.prop] || []));
		},

		writeDay(weekDay, mutator) {
			const list = this.cloneList();

			let entry = list.find((r) => r.week_day === weekDay);
			if (!entry) {
				entry = {
					week_day: weekDay,
					ranges: [],
					repeat: 1
				};
				list.push(entry);
			}

			mutator(entry);

			// drop empty days so the payload stays clean
			const filtered = list.filter((r) => r.ranges && r.ranges.length > 0);

			// keep stable order: Mon..Sun (1..6,0)
			const order = [1, 2, 3, 4, 5, 6, 0];
			filtered.sort((a, b) => {
				return order.indexOf(a.week_day) - order.indexOf(b.week_day);
			});

			this.emit(filtered);
		},

		toggleDay(weekDay) {
			if (this.isDayActive(weekDay)) {
				// turn off
				const list = this.cloneList().filter((r) => r.week_day !== weekDay);
				this.emit(list);
			} else {
				this.writeDay(weekDay, (entry) => {
					entry.ranges = [
						[9*3600, 17*3600 + 59*60]
					];
					entry.repeat = entry.repeat || 1;
				});
			}
		},

		addRange(weekDay) {
			this.writeDay(weekDay, (entry) => {
				entry.ranges.push(
					[9*3600, 9*3600 + 59*60]
				);
			});
		},

		removeRange(weekDay, idx) {
			this.writeDay(weekDay, (entry) => {
				entry.ranges.splice(idx, 1);
			});
		},

		updateRangeStart(weekDay, idx, value) {
			this.writeDay(weekDay, (entry) => {
				const current = entry.ranges[idx];

				var parts = value.split(':');
				var secs = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60;

				entry.ranges[idx] = [
					secs,
					current[1] || 23*3600 + 59*60
				];
			});
		},

		updateRangeEnd(weekDay, idx, value) {
			this.writeDay(weekDay, (entry) => {
				const current = entry.ranges[idx];

				var parts = value.split(':');
				var secs = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60;

				entry.ranges[idx] = [
					current[0] || 0,
					secs
				];
			});
		},

		updateRepeat(weekDay, value) {
			const repeat = Math.max(1, parseInt(value, 10) || 1);
			this.writeDay(weekDay, (entry) => {
				entry.repeat = repeat;
			});
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$purple: $v2-purple
$border: $v2-border
$card: $v2-card-bg
$text: $v2-text
$muted: $v2-text-muted
$bg: #f3f4f6
$font: $v2-font
$r-input: 10px

.g-timing
	font-family: $font

.g-timing__day
	border: 1px solid $border
	border-radius: 12px
	padding: 14px 16px
	background: $card
	margin-bottom: 8px
	transition: all .15s

	&:hover
		border-color: rgba($border, 1)

.g-timing__day--active
	border-color: rgba($purple, 0.35)
	background: rgba($purple, 0.025)

.g-timing__day--inactive
	background: rgba($bg, 0.4)
	border-style: dashed

.g-timing__day-head
	display: flex
	align-items: center
	gap: 14px

.g-timing__toggle
	width: 20px
	height: 20px
	border: 1.5px solid $border
	border-radius: 5px
	display: inline-flex
	align-items: center
	justify-content: center
	background: #fff
	flex-shrink: 0
	cursor: pointer
	padding: 0
	transition: all .15s

	&:hover
		border-color: $purple

	svg
		width: 12px
		height: 12px
		color: #fff

.g-timing__toggle--on
	background: $purple
	border-color: $purple
	color: #fff

.g-timing__day-name
	font-size: 14px
	font-weight: 700
	min-width: 90px
	color: $text

	.g-timing__day--inactive &
		color: $muted
		font-weight: 600

.g-timing__day-summary
	font-size: 11.5px
	font-weight: 500
	color: $muted
	margin-left: auto
	font-family: 'JetBrains Mono', monospace
	letter-spacing: -0.01em

	.g-timing__day--active &
		color: $purple

.g-timing__ranges
	margin-top: 14px
	padding-left: 34px
	display: flex
	flex-direction: column
	gap: 8px

.g-timing__range
	display: grid
	grid-template-columns: 130px 18px 130px 1fr 32px
	gap: 10px
	align-items: center

.g-timing__input
	height: 38px
	padding: 8px 10px
	border: 1px solid $border
	border-radius: $r-input
	font-size: 13px
	font-family: 'JetBrains Mono', monospace
	background: rgba($bg, 0.55)
	color: $text
	transition: all .15s
	width: 100%

	&:focus
		outline: none
		border-color: $purple
		background: #fff
		box-shadow: 0 0 0 3px rgba($purple, 0.14)

.g-timing__input--repeats
	width: 70px
	text-align: center

.g-timing__dash
	text-align: center
	color: $muted
	font-weight: 600

.g-timing__repeats
	display: flex
	align-items: center
	gap: 8px

.g-timing__repeats-label
	font-size: 11px
	color: $muted
	white-space: nowrap
	text-transform: uppercase
	letter-spacing: 0.06em
	font-weight: 600

.g-timing__remove
	width: 32px
	height: 32px
	padding: 0
	border-radius: 8px
	background: transparent
	border: 1px solid $border
	color: $muted
	cursor: pointer
	display: inline-flex
	align-items: center
	justify-content: center
	transition: all .15s

	&:hover
		color: $v2-red
		border-color: rgba($v2-red, 0.35)
		background: rgba($v2-red, 0.06)

	svg
		width: 14px
		height: 14px

.g-timing__add
	display: inline-flex
	align-items: center
	gap: 6px
	padding: 7px 12px
	margin-top: 10px
	margin-left: 34px
	border: 1px dashed rgba($purple, 0.4)
	border-radius: 8px
	font-size: 12px
	font-weight: 600
	color: $purple
	background: transparent
	cursor: pointer
	transition: all .15s
	font-family: $font

	&:hover
		background: rgba($purple, 0.06)
		border-style: solid

	svg
		width: 13px
		height: 13px

// ── Preview ────────────────────────
.g-timing__preview
	margin-top: 22px
	padding: 18px 18px 14px
	background: rgba($bg, 0.6)
	border: 1px solid $border
	border-radius: 12px

.g-timing__preview-head
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 14px
	gap: 12px
	flex-wrap: wrap

.g-timing__preview-title
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $muted

.g-timing__preview-summary
	display: inline-flex
	align-items: center
	gap: 6px
	padding: 5px 10px
	border-radius: 9999px
	font-size: 10px
	font-weight: 700
	letter-spacing: 0.02em
	text-transform: uppercase
	background: rgba($purple, 0.08)
	color: $purple
	border: 1px solid rgba($purple, 0.25)

.g-timing__preview-row
	display: grid
	grid-template-columns: 50px 1fr
	gap: 10px
	align-items: center
	margin-bottom: 4px

.g-timing__preview-label
	font-size: 11px
	font-weight: 600
	color: $muted
	text-align: right

.g-timing__preview-track
	display: grid
	grid-template-columns: repeat(24, 1fr)
	gap: 1px
	height: 22px
	border-radius: 6px
	overflow: hidden
	background: rgba($bg, 0.7)
	border: 1px solid $border

.g-timing__preview-cell
	background: rgba($bg, 0.6)
	transition: background .2s

.g-timing__preview-cell--on
	background: $purple

.g-timing__preview-axis
	display: grid
	grid-template-columns: 50px 1fr
	gap: 10px
	margin-top: 8px
	font-size: 9.5px
	color: $muted
	font-family: 'JetBrains Mono', monospace

.g-timing__preview-label-spacer
	display: block

.g-timing__preview-axis-grid
	display: grid
	grid-template-columns: repeat(24, 1fr)

	span
		text-align: left
		padding-left: 1px

.g-timing__preview-help
	margin-top: 12px
	font-size: 11px
	color: $muted
</style>
